import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaChevronDown } from "react-icons/fa";

const DEFAULT_PALETTE = {
  primaryRed: "#D84E55",
  accentBlue: "#3A86FF",
  textDark: "#1A1A1A",
  textLight: "#4B5563",
  bgLight: "#FFFFFF",
  borderLight: "#E0E0E0",
  white: "#FFFFFF",
};

const SECTION_INNER =
  "w-full max-w-[1400px] 2xl:max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8";

export default function HomeFaqWideSection({
  palette = DEFAULT_PALETTE,
  title = "FAQs related to Bus Tickets Booking",
  subtitle = "Quick answers customers usually ask before booking.",
}) {
  const tabs = useMemo(
    () => [
      { key: "general", label: "General" },
      { key: "ticket", label: "Ticket-related" },
      { key: "payment", label: "Payment" },
      { key: "cancel", label: "Cancellation & Refund" },
    ],
    []
  );

  const data = useMemo(
    () => ({
      general: [
        {
          q: "Do I need to create an account to book tickets?",
          a: "No. You can book as a guest in most cases. But having an account helps you access My Bookings, re-download tickets, and manage cancellations faster.",
        },
        {
          q: "Do I need to print the ticket?",
          a: "No. Your e-ticket (QR/PNR) on your phone is enough. You can also open it anytime from My Bookings.",
        },
        {
          q: "Does online booking cost more?",
          a: "No. Ticket prices are shown transparently. Any service/convenience fee (if applicable) is clearly displayed before you pay.",
        },
      ],
      ticket: [
        {
          q: "How do I book a bus ticket on Routesbook.lk?",
          a: [
            "Select From, To, and Date.",
            "Compare buses (time, price, type, operator).",
            "Select seats on the live seat layout.",
            "Choose boarding & dropping points.",
            "Enter passenger details and pay securely.",
            "Download your e-ticket from My Bookings.",
          ],
        },
        {
          q: "I lost my ticket. What should I do?",
          a: "Open My Bookings and re-download your ticket. If you still can’t find it, contact support with your booking details.",
        },
        {
          q: "Can I change my journey date after booking?",
          a: "Rescheduling depends on the operator policy. If supported, you can cancel (as per policy) and rebook for a new date.",
        },
      ],
      payment: [
        {
          q: "Is it safe to pay online?",
          a: "Yes. Payments are processed via a trusted payment gateway. Routesbook.lk does not store your full card details.",
        },
        {
          q: "What payment methods are available?",
          a: "Available methods depend on the payment gateway (cards, bank transfers, wallets, etc.). All supported methods are shown on the payment page.",
        },
        {
          q: "Money deducted but booking failed — what now?",
          a: "Most gateways auto-refund failed transactions. If you don’t receive a refund within the gateway’s timeline, contact support with the payment reference/receipt.",
        },
      ],
      cancel: [
        {
          q: "Can I cancel my booking online?",
          a: "Cancellation availability depends on the operator’s policy. If cancellation is allowed, the refund amount and timeline follow the policy shown on your booking/ticket.",
        },
        {
          q: "How is my refund processed?",
          a: "Refunds are typically credited back to your original payment method (based on gateway rules) and the operator cancellation policy.",
        },
        {
          q: "What if the bus operator cancels the trip?",
          a: "If a trip is cancelled by the operator, your booking is eligible for refund according to the operator/Routesbook policy. You can also contact support for help.",
        },
        {
          q: "I missed the bus. Do I get a refund?",
          a: "Usually no, unless the operator policy explicitly allows it or the service was affected by operator-side cancellation/delay rules stated in policy.",
        },
      ],
    }),
    []
  );

  const [activeTab, setActiveTab] = useState("general");
  const [openIndex, setOpenIndex] = useState(0);

  const items = data[activeTab] || [];

  const renderAnswer = (a) => {
    if (Array.isArray(a)) {
      return (
        <ul className="list-disc pl-5 space-y-1">
          {a.map((x, i) => (
            <li key={i}>{x}</li>
          ))}
        </ul>
      );
    }
    return <p>{a}</p>;
  };

  return (
    <section className="w-screen relative left-1/2 ml-[-50vw] overflow-hidden">
      {/* Background like your Home premium sections */}
      <div
        className="py-14 md:py-16"
        style={{
          background:
            "linear-gradient(180deg, rgba(58,134,255,0.06) 0%, rgba(216,78,85,0.06) 45%, rgba(255,255,255,1) 100%)",
        }}
      >
        {/* floating blobs */}
        <motion.div
          animate={{ y: [0, -10, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -top-14 left-10 w-52 h-52 rounded-full blur-3xl"
          style={{ backgroundColor: "rgba(58,134,255,0.16)" }}
        />
        <motion.div
          animate={{ y: [0, 12, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-10 right-12 w-60 h-60 rounded-full blur-3xl"
          style={{ backgroundColor: "rgba(216,78,85,0.16)" }}
        />

        <div className={`${SECTION_INNER} relative`}>
          {/* Card wrapper */}
          <div
            className="rounded-2xl border bg-white shadow-sm overflow-hidden"
            style={{ borderColor: palette.borderLight }}
          >
            {/* Header */}
            <div
              className="px-5 sm:px-8 py-6 border-b"
              style={{ borderColor: palette.borderLight }}
            >
              <h2
                className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight"
                style={{ color: palette.textDark }}
              >
                {title}
              </h2>
              <p className="mt-2 text-sm sm:text-base text-gray-600">
                {subtitle}
              </p>

              {/* Tabs */}
              <div className="mt-5 border-b" style={{ borderColor: "#EEF2F7" }}>
                <div className="flex gap-6 overflow-x-auto no-scrollbar">
                  {tabs.map((t) => {
                    const active = t.key === activeTab;
                    return (
                      <button
                        key={t.key}
                        type="button"
                        onClick={() => {
                          setActiveTab(t.key);
                          setOpenIndex(0);
                        }}
                        className="whitespace-nowrap pb-3 text-sm font-semibold -mb-px border-b-2 transition"
                        style={{
                          color: active ? palette.primaryRed : "#374151",
                          borderColor: active ? palette.primaryRed : "transparent",
                        }}
                      >
                        {t.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Accordion list (Redbus-like divider style) */}
            <div className="px-5 sm:px-8">
              <div className="divide-y" style={{ borderColor: "#EEF2F7" }}>
                {items.map((item, idx) => {
                  const isOpen = openIndex === idx;

                  return (
                    <div key={item.q} className="py-4 sm:py-5">
                      <button
                        type="button"
                        className="w-full flex items-start justify-between gap-6 text-left"
                        onClick={() => setOpenIndex(isOpen ? -1 : idx)}
                        aria-expanded={isOpen}
                      >
                        <div className="min-w-0">
                          <div className="text-[15px] sm:text-base font-semibold text-gray-900 leading-6">
                            {item.q}
                          </div>
                        </div>

                        <span className="shrink-0 pt-1">
                          <FaChevronDown
                            className="transition-transform"
                            style={{
                              color: "#6B7280",
                              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                            }}
                          />
                        </span>
                      </button>

                      <AnimatePresence initial={false}>
                        {isOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.18 }}
                            className="overflow-hidden"
                          >
                            <div className="pt-3 pr-8 text-sm sm:text-[15px] text-gray-600 leading-7">
                              {renderAnswer(item.a)}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>

              {/* Footer hint (optional, matches your premium cards) */}
              <div className="py-6">
                <div
                  className="rounded-xl border bg-gray-50 p-4 text-sm text-gray-700"
                  style={{ borderColor: "#EEF2F7" }}
                >
                  <span className="font-semibold">Need help?</span> Email us anytime at{" "}
                  <span style={{ color: palette.primaryRed, fontWeight: 700 }}>
                    support@routesbook.lk
                  </span>
                </div>
              </div>
            </div>
          </div>
          {/* end card */}
        </div>
      </div>
    </section>
  );
}
